import type { StackProps } from '@chakra-ui/react';
import { Flex, HStack, Text } from '@chakra-ui/react';
import { TextLink } from '@time-line/components/common';
import { Soundbars } from '@time-line/components/icons';
import { format } from 'date-fns';
import { BLOG_CATEGORIES } from 'lib/sanity';
import NextLink from 'next/link';
import type { ReactNode } from 'react';
import type { BlogCategory } from 'types/cms/blog/BlogCategory';

interface BlogDetailsProps extends StackProps {
  categories?: BlogCategory[];
  publishedAt?: string;
  updatedAt?: string;
  duration?: string;
  isCategoryClickable?: boolean;
}

const Time = ({ date, children }: { date: string; children?: ReactNode }) => {
  const formattedDate = Date.parse(date);

  return (
    <Text size="overline" as="span" color="blackAlpha.700">
      {children}
      <time dateTime={format(formattedDate, 'yyyy-MM-dd')}>
        {format(formattedDate, 'LLL dd, yyyy')}
      </time>
    </Text>
  );
};

const StackDivider = () => (
  <Text size="overline" px="4xs" color="blackAlpha.700">
    ·
  </Text>
);

export function BlogDetails({
  categories,
  publishedAt,
  updatedAt,
  duration,
  isCategoryClickable,
  ...props
}: BlogDetailsProps) {
  const blogCategories = Object.values(BLOG_CATEGORIES);

  return (
    <HStack wrap="wrap" divider={<StackDivider />} {...props}>
      {categories?.map((category, index) => {
        const match = blogCategories.find(({ ID }) => ID === category);
        return match && isCategoryClickable ? (
          <TextLink
            key={index}
            as={NextLink}
            href={match.ROUTE}
            textProps={{ size: 'overline' }}
          >
            {match && match.FORMAT}
          </TextLink>
        ) : (
          <Text key={index} size="overline" as="span">
            {match && match.FORMAT}
          </Text>
        );
      })}

      {duration && (
        <Flex alignItems="center" gap="4xs" color="blackAlpha.700">
          <Soundbars boxSize="2xs" />
          <Text size="overline">{duration}</Text>
        </Flex>
      )}

      <HStack divider={<StackDivider />}>
        {publishedAt && <Time date={publishedAt} />}
        {updatedAt && <Time date={updatedAt}>Updated </Time>}
      </HStack>
    </HStack>
  );
}
